import { configureStore } from '@reduxjs/toolkit';

import toolsApi from '../apis/toolsApi';
import { authApi } from '../modules/auth/authApi';
import { systemEmailApi } from '../modules/content/systemEmails/systemEmailApi';
import systemEmailSlice from '../modules/content/systemEmails/systemEmailSlice';
import { dynamicMenuApi } from '../modules/dynamicMenu/dynamicMenuApi';
import dynamicMenuSlice from '../modules/dynamicMenu/dynamicMenuSlice';
import { logsApi } from '../modules/errorLogs/logsApi';
import changePasswordApi from '../modules/home/changePassword/changePasswordApi';
import editProfileApi from '../modules/home/editProfile/editProfileApi';
import menuSlice from '../modules/menu/menuApi';
import { permissionApi } from '../modules/roleManagement/permissions/permissionApi';
import permissionSlice from '../modules/roleManagement/permissions/permissionSlice';
import roleApi from '../modules/roleManagement/role/roleApi';
import { settingsApi } from '../modules/settings/settingsApi';
import settingsSlice from '../modules/settings/settingsSlice';
import { profileApi } from '../modules/user/profileApi';
import profileSlice from '../modules/user/profileSlice';
import adminApi from '../modules/userManagement/admin/adminApi';
import adminSlice from '../modules/userManagement/admin/adminSlice';
import usersApi from '../modules/userManagement/users/usersApi';
import eventsApi from '../modules/eventManagement/events/eventsApi';
import dashboardApi from '../modules/dashboard/dashboardApi';
import loadingSlice from '../utils/loadingSlice';
import listingPreferencesSlice from '../utils/listingPreferencesSlice';
import notificationsApi from '../modules/notifications/notificationsApi';
import emailNotificationsApi from '../modules/configurations/emailNotifications/emailNotificationsApi';
import systemNotificationsApi from '../modules/configurations/systemNotifications/systemNotificationsApi';
import pushNotificationsApi from '../modules/configurations/pushNotifications/pushNotificationsApi';
import systemSettingsApi from '../modules/configurations/systemSettings/systemSettingsApi';
import { autocompleteApi } from '../modules/common/autocompleteApi';
import { staticPagesApi } from '../modules/content/staticPage/staticPagesApi';
import { combineReducers, type Action } from '@reduxjs/toolkit';

const rootReducer = combineReducers({
  dynamicMenuReducer: dynamicMenuSlice.reducer,
  loading: loadingSlice.reducer,
  listingPreferences: listingPreferencesSlice.reducer,
  profile: profileSlice.reducer,
  menu: menuSlice.reducer,
  admin: adminSlice.reducer,
  permission: permissionSlice.reducer,
  systemEmail: systemEmailSlice.reducer,
  settings: settingsSlice.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [dynamicMenuApi.reducerPath]: dynamicMenuApi.reducer,
  [logsApi.reducerPath]: logsApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [editProfileApi.reducerPath]: editProfileApi.reducer,
  [changePasswordApi.reducerPath]: changePasswordApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [roleApi.reducerPath]: roleApi.reducer,
  [permissionApi.reducerPath]: permissionApi.reducer,
  [toolsApi.reducerPath]: toolsApi.reducer,
  [systemEmailApi.reducerPath]: systemEmailApi.reducer,
  [settingsApi.reducerPath]: settingsApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [emailNotificationsApi.reducerPath]: emailNotificationsApi.reducer,
  [systemNotificationsApi.reducerPath]: systemNotificationsApi.reducer,
  [pushNotificationsApi.reducerPath]: pushNotificationsApi.reducer,
  [systemSettingsApi.reducerPath]: systemSettingsApi.reducer,
  [autocompleteApi.reducerPath]: autocompleteApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [eventsApi.reducerPath]: eventsApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [staticPagesApi.reducerPath]: staticPagesApi.reducer,
});

const resettableRootReducer = (state: any, action: Action) => {
  if (action.type === 'profile/logout') {
    state = undefined;
  }
  return rootReducer(state, action);
};

export const store = configureStore({
  reducer: resettableRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      dynamicMenuApi.middleware,
      logsApi.middleware,
      profileApi.middleware,
      editProfileApi.middleware,
      changePasswordApi.middleware,
      adminApi.middleware,
      roleApi.middleware,
      permissionApi.middleware,
      toolsApi.middleware,
      systemEmailApi.middleware,
      settingsApi.middleware,
      notificationsApi.middleware,
      emailNotificationsApi.middleware,
      systemNotificationsApi.middleware,
      pushNotificationsApi.middleware,
      systemSettingsApi.middleware,
      autocompleteApi.middleware,
      usersApi.middleware,
      eventsApi.middleware,
      dashboardApi.middleware,
      staticPagesApi.middleware
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
